import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, Clock, Phone, ArrowRightLeft } from "lucide-react";
import { cn } from "@/lib/utils";

export const getStatusBadge = (status: string) => {
  const variants: Record<string, { icon: typeof CheckCircle; className: string; label: string }> = {
    active: {
      icon: CheckCircle,
      className: "bg-green-100 text-green-800 border-green-200",
      label: "Active",
    },
    "in-use": {
      icon: CheckCircle,
      className: "bg-green-100 text-green-800 border-green-200",
      label: "In Use",
    },
    pending: {
      icon: Clock,
      className: "bg-yellow-100 text-yellow-800 border-yellow-200",
      label: "Pending",
    },
    port_requested: {
      icon: Clock,
      className: "bg-genie-accent/10 text-genie-accent border-genie-accent/20",
      label: "Port Requested",
    },
    port_pending: {
      icon: Clock,
      className: "bg-genie-primary/10 text-genie-primary border-genie-primary/20",
      label: "Port Pending",
    },
    released: {
      icon: XCircle,
      className: "bg-gray-100 text-gray-600 border-gray-200",
      label: "Released",
    },
    failed: {
      icon: XCircle,
      className: "bg-red-100 text-red-800 border-red-200",
      label: "Failed",
    },
  };

  const variant = variants[status.toLowerCase()] || {
    icon: Clock,
    className: "bg-gray-100 text-gray-600 border-gray-200",
    label: status,
  };

  const Icon = variant.icon;

  return (
    <Badge className={cn(variant.className, "flex items-center gap-1 border text-xs")}>
      <Icon className="h-3 w-3" />
      {variant.label}
    </Badge>
  );
};

export const getTypeBadge = (isTransfer: boolean | null, metadata?: Record<string, any> | null) => {
  if (isTransfer) {
    return (
      <Badge className="bg-purple-100 text-purple-800 border-purple-200 border flex items-center gap-1 text-xs">
        <ArrowRightLeft className="h-3 w-3" />
        Transfer
      </Badge>
    );
  }

  if (metadata?.source === "ported") {
    return (
      <Badge className="bg-genie-secondary/10 text-genie-secondary border-genie-secondary/20 border flex items-center gap-1 text-xs">
        <Phone className="h-3 w-3" />
        Existing
      </Badge>
    );
  }

  return (
    <Badge className="bg-blue-100 text-blue-800 border-blue-200 border flex items-center gap-1 text-xs">
      <Phone className="h-3 w-3" />
      Main
    </Badge>
  );
};

export const formatPhoneNumber = (phoneNumber: string): string => {
  // Remove all non-digit characters except +
  const cleaned = phoneNumber.replace(/[^\d+]/g, "");

  // If it starts with +1 (US/Canada), format as +1 (XXX) XXX-XXXX
  if (cleaned.startsWith("+1") && cleaned.length === 12) {
    return `+1 (${cleaned.slice(2, 5)}) ${cleaned.slice(5, 8)}-${cleaned.slice(8)}`;
  }

  // If it's 10 digits (US without country code)
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  // For international numbers, just add spaces for readability
  if (cleaned.startsWith("+")) {
    const countryCode = cleaned.slice(0, cleaned.length - 10);
    const number = cleaned.slice(-10);
    if (number.length === 10) {
      return `${countryCode} (${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
    }
  }

  // Return as-is if no pattern matches
  return phoneNumber;
};

export const getCountryName = (code: string | null): string => {
  if (!code) return "Unknown";

  const countries: Record<string, string> = {
    US: "United States",
    CA: "Canada",
    GB: "United Kingdom",
    AU: "Australia",
    NZ: "New Zealand",
    PH: "Philippines",
    DE: "Germany",
    FR: "France",
    ES: "Spain",
    IT: "Italy",
    NL: "Netherlands",
    BE: "Belgium",
    CH: "Switzerland",
    AT: "Austria",
    IE: "Ireland",
    SE: "Sweden",
    NO: "Norway",
    DK: "Denmark",
    FI: "Finland",
    PT: "Portugal",
    PL: "Poland",
    MX: "Mexico",
    BR: "Brazil",
    AR: "Argentina",
    CL: "Chile",
    CO: "Colombia",
    JP: "Japan",
    KR: "South Korea",
    SG: "Singapore",
    HK: "Hong Kong",
    IN: "India",
    ZA: "South Africa",
  };

  return countries[code] || code;
};

export const getCountryFlag = (code: string | null): string => {
  if (!code) return "🌐";

  // Convert country code to flag emoji using regional indicator symbols
  const codePoints = [...code.toUpperCase()].map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};
